import React, { useState, useContext, useEffect } from 'react';
import { ApiContext } from '../../App';
import { toast } from 'react-toastify';
import {
  X,
  Mail,
  ArrowRight,
  Trash2,
  Users,
  ChevronDown
} from 'lucide-react';
import SendEmailModal from '../SendEmailModal';
import ConfirmationDialog from '../utils/ConfirmationDialog';

const RecruitmentBulkActions = ({ selectedCandidates, positionId, onClearSelection, onRefresh, onUpdateCandidates }) => {
  const API = useContext(ApiContext);
  const [showSendEmailModal, setShowSendEmailModal] = useState(false);
  const [emailData, setEmailData] = useState({
    subject: '',
    message: ''
  });
  const [loading, setLoading] = useState(false);
  const [showMoveDropdown, setShowMoveDropdown] = useState(false);
  const [individualIdsForEmail, setIndividualIdsForEmail] = useState([]);
  const [recruitmentSteps, setRecruitmentSteps] = useState([]);
  const [showRemoveDialog, setShowRemoveDialog] = useState(false);

  // Récupérer les étapes du processus au montage du composant
  useEffect(() => {
    const fetchPositionSteps = async () => {
      try {
        const response = await API.get(`/api/positions/${positionId}`);
        const positionData = response.data;

        // Filtrer les étapes actives et les trier par ordre
        const activeSteps = positionData.steps
          .filter(step => step.active)
          .sort((a, b) => a.order - b.order);

        setRecruitmentSteps(activeSteps);
      } catch (error) {
        console.error('Error fetching position steps:', error);
        toast.error('Erreur lors de la récupération des étapes');
      }
    };

    fetchPositionSteps();
  }, [positionId, API]);


  const handleMoveToStage = async (step) => {
    setLoading(true);
    try {
      // Récupérer les données des candidats pour obtenir leurs individualIds
      const positionResponse = await API.get(`/api/positions/${positionId}/candidates`);
      const candidates = positionResponse.data;

      const selectedCandidateObjects = candidates.filter(candidate =>
        selectedCandidates.includes(candidate._id)
      );

      // Extraire les individualIds
      const individualIds = selectedCandidateObjects.map(candidate => candidate.individual._id);

      // Utiliser la route bulk pour déplacer tous les candidats en une seule fois
      const response = await API.post(`/api/positions/${positionId}/candidates/bulk-move`, {
        individualIds,
        stageId: step._id
      });

      if (response.data.success) {
        const { successCount, errorCount, targetStage } = response.data;

        // Afficher le résultat
        if (successCount > 0) {
          toast.success(`${successCount} candidat(s) déplacé(s) vers ${targetStage}`);
        }

        if (errorCount > 0) {
          console.error('Errors during move:', response.data.errors);
          toast.warning(`${errorCount} erreur(s) lors du déplacement`);
        }

        // Recharger la page pour afficher les changements
        onClearSelection();
        onRefresh();
      }
    } catch (error) {
      console.error('Error moving candidates:', error);
      toast.error(`Erreur lors du déplacement: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
      setShowMoveDropdown(false);
    }
  };

  const handleRemoveCandidates = async () => {
    setLoading(true);
    setShowRemoveDialog(false);

    try {
      // Récupérer les données des candidats pour obtenir leurs individualIds
      const positionResponse = await API.get(`/api/positions/${positionId}/candidates`);
      const candidates = positionResponse.data;

      const selectedCandidateObjects = candidates.filter(candidate =>
        selectedCandidates.includes(candidate._id)
      );

      // Extraire les individualIds
      const individualIds = selectedCandidateObjects.map(candidate => candidate.individual._id);

      // Utiliser la route bulk pour retirer tous les candidats en une seule fois
      const response = await API.post(`/api/positions/${positionId}/candidates/bulk-remove`, {
        individualIds: individualIds
      });

      if (response.data.success) {
        const { successCount, errorCount } = response.data.results;

        // Afficher le résultat
        if (successCount > 0) {
          toast.success(`${successCount} candidat(s) retiré(s) du processus`);
        }

        if (errorCount > 0) {
          console.error('Errors during removal:', response.data.errors);
          toast.warning(`${errorCount} erreur(s) lors du retrait`);
        }

        // Mettre à jour l'état local
        if (onUpdateCandidates) {
          onUpdateCandidates(selectedCandidateObjects, null, 'remove');
        } else {
          onRefresh(); // Fallback au refresh complet
        }

        onClearSelection();
      }
    } catch (error) {
      console.error('Error removing candidates:', error);
      toast.error(`Erreur lors de la suppression: ${error.response?.data?.error || error.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleOpenEmailModal = async () => {
    setLoading(true);
    try {
      // Récupérer les données des candidats pour obtenir leurs individualIds
      const positionResponse = await API.get(`/api/positions/${positionId}/candidates`);
      const candidates = positionResponse.data;

      const selectedCandidateObjects = candidates.filter(candidate =>
        selectedCandidates.includes(candidate._id)
      );

      // Extraire les individual IDs
      const individualIds = selectedCandidateObjects.map(candidate => candidate.individual._id);

      setIndividualIdsForEmail(individualIds);
      setShowSendEmailModal(true);
    } catch (error) {
      console.error('Error fetching candidate data:', error);
      toast.error('Erreur lors de la récupération des données des candidats');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4  ">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <div className="bg-blue-100 p-2 rounded-lg">
              <Users className="text-blue-600" size={20} />
            </div>
            <div>
              <h3 className="text-sm font-medium text-blue-900">
                {selectedCandidates.length} candidat(s) sélectionné(s)
              </h3>
              <p className="text-xs text-blue-700">
                Choisissez une action à appliquer
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {/* Bouton Email */}
            <button
              onClick={handleOpenEmailModal}
              disabled={loading}
              className="flex items-center space-x-1 px-3 py-2 bg-green-600 hover:bg-green-700 text-white rounded-lg transition-colors text-sm"
            >
              <Mail size={14} />
              <span>Envoyer un email</span>
            </button>

            {/* Bouton Déplacer */}
            <div className="relative">
              <button
                onClick={() => setShowMoveDropdown(!showMoveDropdown)}
                disabled={loading}
                className="flex items-center space-x-1 px-3 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg transition-colors text-sm"
              >
                <ArrowRight size={14} />
                <span>Déplacer</span>
                <ChevronDown size={14} />
              </button>
              
              {showMoveDropdown && (
                <div className="absolute right-0 top-10 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-20">
                  <div className="p-2">
                    <p className="text-xs text-gray-500 mb-2">Déplacer vers l'étape :</p>
                    {recruitmentSteps.map((step) => (
                      <button
                        key={step._id}
                        onClick={() => handleMoveToStage(step)}
                        className="w-full text-left px-2 py-1 text-sm hover:bg-gray-100 rounded text-gray-700"
                      >
                        {step.name}
                      </button>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* Bouton Supprimer */}
            <button
              onClick={() => setShowRemoveDialog(true)}
              disabled={loading}
              className="flex items-center space-x-1 px-3 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm"
            >
              <Trash2 size={14} />
              <span>Retirer</span>
            </button>

            {/* Bouton Fermer */}
            <button
              onClick={onClearSelection}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X size={16} className="text-gray-500" />
            </button>
          </div>
        </div>
      </div>

      {/* Modal Email */}
      {showSendEmailModal && (
        <SendEmailModal
          recipients={individualIdsForEmail}
          onClose={() => setShowSendEmailModal(false)}
          onSend={() => setShowSendEmailModal(false)}/>
      )}

      {/* Overlay pour fermer le dropdown */}
      {showMoveDropdown && (
        <div
          className="fixed inset-0 z-10"
          onClick={() => setShowMoveDropdown(false)}
        />
      )}

      {/* Confirmation Dialog for Remove Candidates */}
      <ConfirmationDialog
        isOpen={showRemoveDialog}
        onClose={() => setShowRemoveDialog(false)}
        onConfirm={handleRemoveCandidates}
        title="Retirer les candidats"
        message={`Êtes-vous sûr de vouloir retirer ${selectedCandidates.length} candidat(s) de ce processus ? Cette action est irréversible.`}
        confirmText="Retirer"
        cancelText="Annuler"
        isDangerous={true}
      />
    </>
  );
};

export default RecruitmentBulkActions;